import {Component, computed, effect, ElementRef, inject, signal, ViewChild, DestroyRef, ChangeDetectionStrategy, untracked} from "@angular/core"
import {CM_LOGGER_ROUTING} from "@app/common/services/logging"
import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from "@angular/cdk/menu"
import {ActivatedRoute, NavigationEnd, Router, RouterModule} from "@angular/router"
import {UserThumbnailComponent} from "@platform/components/users/user-thumbnail/user-thumbnail.component"
import {AuthService} from "@common/services/auth/auth.service"
import {ConnectedPosition} from "@angular/cdk/overlay"
import {AppRoutes, getOrganizationQueryParams} from "@app/platform/config/navigation-config"
import {SiloService} from "@common/services/auth/silo.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {PictureCreationService} from "@common/services/picture-creation/picture-creation.service"
import {AccountType, ContentTypeModel, DataObjectAssignmentType, MembershipRole, OrganizationType, SystemRole} from "@generated"
import {
    SideNavigationAllOrganizationsGQL,
    SideNavigationRenderServerDownloadsGQL,
    SideNavigationColormassOrganizationGQL,
    SideNavigationOrganizationFragment,
    SideNavigationUserMembershipsGQL,
    CreateProfilePictureAssignmentGQL,
} from "@app/common/components/navigation/side-navigation/side-navigation.generated"
import {fetchIgnoringErrors, fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {IsDefined} from "@cm/utils"
import {filter, map} from "rxjs"
import {isApple} from "@cm/browser-utils"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {toSignal} from "@angular/core/rxjs-interop"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"
import {BlenderAddonService} from "@common/services/blender-addon/blender-addon.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-side-navigation",
    templateUrl: "./side-navigation.component.html",
    styleUrls: ["./side-navigation.component.scss"],
    imports: [CdkMenu, CdkMenuItem, CdkMenuTrigger, UserThumbnailComponent, RouterModule, ToggleComponent],
})
export class SideNavigationComponent {
    private readonly renderServerTargets: Record<string, {label: string; icon: string}> = {
        windows: {label: "Windows", icon: "fa-brands fa-windows"},
        darwin: {label: "macOS", icon: "fa-brands fa-apple"},
        linux: {label: "Linux", icon: "fa-brands fa-linux"},
    }
    readonly $renderServerDownloads = signal<{label: string; icon: string; target: string; version: string; url: string}[]>([])
    readonly $blenderAddonDownload = inject(BlenderAddonService).$download

    private readonly logger = inject(CM_LOGGER_ROUTING)
    @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>

    readonly auth = inject(AuthService)
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)
    readonly Routes = AppRoutes
    readonly permission = inject(PermissionsService)
    readonly silo = inject(SiloService)
    readonly refresh = inject(RefreshService)
    readonly uploadGqlService = inject(UploadGqlService)
    readonly pictureCreation = inject(PictureCreationService)
    readonly localStorageService = inject(LocalStorageService)

    readonly $navigationMode = signal<"online" | "offline">(this.localStorageService.developmentMode ? "online" : "offline")

    readonly $can = this.permission.$to
    public readonly $canSeeJobsMenu = computed(
        () =>
            this.$can().read.menu("jobs") ||
            this.$can().read.menu("jobFarmInstances") ||
            this.$can().read.menu("jobFarmGroups") ||
            this.$can().read.menu("taskTypes"),
    )

    readonly $showOnlineLinks = computed(() => {
        const canReadOnlineLinks = this.$can().read.menu("onlineLinks")
        const canToggle = this.$can().update.menu("online")
        // Show online links if user has permission and either can't toggle or has toggled to online mode
        return canReadOnlineLinks && (!canToggle || this.$navigationMode() === "online")
    })

    readonly $currentPath = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects),
        ),
        {initialValue: this.router.url},
    )
    readonly $isShowingAllMaterials = computed(() => {
        const url = this.$currentPath()
        this.logger.debug("url", url)
        return url === "/materials"
    })

    toggleNavigationMode(): void {
        this.$navigationMode.update((navigationMode) => (navigationMode === "online" ? "offline" : "online"))
    }

    private readonly sideNavigationColormassOrganization = inject(SideNavigationColormassOrganizationGQL)
    private readonly sideNavigationAllOrganizations = inject(SideNavigationAllOrganizationsGQL)
    private readonly sideNavigationUserMemberships = inject(SideNavigationUserMembershipsGQL)
    private readonly renderServerDownloadsGql = inject(SideNavigationRenderServerDownloadsGQL)
    private readonly createProfilePictureAssignment = inject(CreateProfilePictureAssignmentGQL)
    private readonly destroyRef = inject(DestroyRef)
    readonly $availableMemberships = signal<{id: string; role: MembershipRole; organization: SideNavigationOrganizationFragment}[]>([])
    readonly $availableOrganizations = signal<SideNavigationOrganizationFragment[]>([])

    readonly $canReadAuthenticateForAnyOrganizationMenu = computed(() => this.$can().read.menu("authenticateForAnyOrganization"))
    private readonly $userId = computed(() => this.auth.$user()?.id)
    readonly $organizationQueryParams = computed(() => getOrganizationQueryParams(this.silo.$silo()))
    readonly SystemRole = SystemRole
    readonly AccountType = AccountType

    constructor() {
        effect(() => {
            const userId = this.auth.$user()?.id
            if (userId !== undefined) void this.loadOrganizations(this.$canReadAuthenticateForAnyOrganizationMenu(), userId)
        })
        // The downloads query requires a login, and anonymous visitors see this navigation on the public
        // pages, so (re)load it whenever the logged-in user changes instead of once at construction.
        effect(() => {
            const userId = this.$userId()
            untracked(() => {
                if (userId === undefined) {
                    this.$renderServerDownloads.set([])
                } else {
                    void this.loadRenderServerDownloads()
                }
            })
        })
    }

    private async loadRenderServerDownloads() {
        const result = await fetchThrowingErrors(this.renderServerDownloadsGql)({}).catch(() => null)
        if (!result) return
        this.$renderServerDownloads.set(
            result.renderServerDownloads
                .filter(({target}) => target in this.renderServerTargets)
                .map(({target, version, url}) => ({...this.renderServerTargets[target], target, version, url})),
        )
    }

    readonly menuPositionUser: ConnectedPosition[] = [
        {
            originX: "end",
            originY: "bottom",
            overlayX: "start",
            overlayY: "bottom",
            offsetX: 6,
            offsetY: -8,
        },
    ]

    readonly menuPositionCreate: ConnectedPosition[] = [
        {
            originX: "end",
            originY: "center",
            overlayX: "start",
            overlayY: "center",
            offsetX: 6,
        },
    ]

    readonly menuPositionNested: ConnectedPosition[] = [
        {
            originX: "end",
            originY: "top",
            overlayX: "start",
            overlayY: "top",
            offsetX: 20,
        },
    ]

    readonly menuPositionSystemRoles: ConnectedPosition[] = [
        {
            originX: "end",
            originY: "center",
            overlayX: "start",
            overlayY: "top",
            offsetX: 20,
            offsetY: -32,
        },
    ]
    async navigateToCreatePicture(event: MouseEvent) {
        const setId = this.route.snapshot.queryParamMap.get("setId") ?? undefined
        const projectId = this.route.snapshot.queryParamMap.get("projectId") ?? undefined

        const newTab = event.button === 1 || (event.ctrlKey && !isApple) || (event.metaKey && isApple)
        await this.pictureCreation.createPictureAndNavigate({setId, projectId, newTab})
    }

    isRouteActive(route: string): boolean {
        const currentUrl = this.$currentPath()
        if (route === this.Routes.Home) {
            return currentUrl === "/" || currentUrl.startsWith(this.Routes.Home)
        }
        return currentUrl.startsWith(route)
    }

    readonly $isOtherRouteActive = computed(() => {
        const currentUrl = this.$currentPath()

        // Check root route and login route first
        if (currentUrl === "/" || currentUrl.startsWith("/login")) {
            return false
        }

        // Main routes depend on navigation mode
        const mainRoutes = this.$showOnlineLinks()
            ? [this.Routes.Home, this.Routes.Materials, this.Routes.Products, this.Routes.Scenes]
            : [this.Routes.Home, this.Routes.Materials, this.Routes.Models, this.Routes.Assets]

        // Check if current URL matches any of the main navigation items
        return !mainRoutes.some((route) => currentUrl.startsWith(route))
    })

    async loadOrganizations(canReadAuthenticateForAnyOrganizationMenu: boolean, userId: string) {
        if (canReadAuthenticateForAnyOrganizationMenu) {
            this.$availableMemberships.set([])
            const {organizations} = await fetchThrowingErrors(this.sideNavigationAllOrganizations)(
                {
                    filter: {visibleInFilters: true, isActive: true},
                },
                {context: {withoutSilo: true}},
            )
            this.$availableOrganizations.set([...organizations.filter(IsDefined)])
        } else {
            this.$availableOrganizations.set([])
            const {user} = await fetchThrowingErrors(this.sideNavigationUserMemberships)({id: userId}, {context: {withoutSilo: true}})
            this.$availableMemberships.set(user.memberships)
        }
    }

    async activateSelfService(accountType: AccountType) {
        await this.silo.activateSelfService(accountType)
    }

    async activateSystemRole(systemRole: SystemRole) {
        await this.silo.activateSystemRole(systemRole)
        const colormassOrganizations = await fetchIgnoringErrors(this.sideNavigationColormassOrganization)()
        const colormassId = colormassOrganizations?.organizations?.[0]?.id
        await this.router.navigate([AppRoutes.Home], {
            queryParams: colormassId ? {organizationId: colormassId} : {},
            queryParamsHandling: "replace",
        })
    }

    async activateOrganization(organization: SideNavigationOrganizationFragment): Promise<void> {
        this.silo.activateOrganization(organization)
        await this.router.navigate([AppRoutes.Home], {
            queryParams: {organizationId: organization.id},
            queryParamsHandling: "replace",
        })
    }

    async activateMembership(membership: {id: string; role: MembershipRole; organization: SideNavigationOrganizationFragment}): Promise<void> {
        this.silo.activateMembership(membership)
        this.refresh.all()
        const organizationId = membership.organization?.id ?? null
        await this.router.navigate(["pictures"], {queryParams: {tab: "projects", organizationId}, queryParamsHandling: "replace"})
    }

    organizationTypeName(organization: SideNavigationOrganizationFragment) {
        switch (organization.type) {
            case OrganizationType.EnterpriseCloud:
                return "Enterprise cloud"
            default:
                return undefined
        }
    }

    readonly $showOrganizationSwitcher = computed(
        () =>
            this.$canReadAuthenticateForAnyOrganizationMenu() ||
            (this.$can().read.menu("authenticateForOrganization") && this.$availableMemberships().length > 1),
    )

    logout() {
        this.auth.logOut()
        void this.router.navigate([AppRoutes.Login])
    }

    openOrganizationSettings() {
        void this.router.navigate(["/settings/organization"])
    }

    triggerFileUpload() {
        // Clear the input value to ensure change event fires even if same file is selected
        this.fileInput.nativeElement.value = ""
        this.fileInput.nativeElement.click()
    }

    async onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement

        if (input.files && input.files.length > 0) {
            const file = input.files[0]
            const userId = this.auth.$user()?.id
            const organizationId = this.silo.$silo()?.organization?.id

            if (!userId || !organizationId) {
                this.logger.error("User ID or organization ID not available")
                return
            }

            try {
                // Create and upload the data object
                const dataObject = await this.uploadGqlService.createAndUploadDataObject(
                    file,
                    {
                        organizationId,
                    },
                    {
                        showUploadToolbar: false,
                        processUpload: true,
                    },
                )

                // Create the ProfilePicture data object assignment
                await mutateThrowingErrors(this.createProfilePictureAssignment)({
                    input: {
                        contentTypeModel: ContentTypeModel.User,
                        type: DataObjectAssignmentType.ProfilePicture,
                        dataObjectId: dataObject.id,
                        objectId: userId,
                    },
                })

                // Refresh the specific user to show the new profile picture
                const currentUser = this.auth.$user()
                if (currentUser) {
                    this.refresh.item({id: currentUser.id, __typename: ContentTypeModel.User})
                }
            } catch (error) {
                this.logger.error("Upload failed:", error)
            }
        }
    }
}
